import React, { useState, useEffect, useRef } from "react";
import Multiselect from "multiselect-react-dropdown";

const MultiSelectDropdown = ({
  name, // ✅ Field name comes from parent
  id = "",
  options = [],
  displayValue = "value",
  placeholder = "Select options",
  showCheckbox = true,
  handleChange,
  label,
  ariaLabel = "",
  required = false,
  onBlur,
  errorMessage,
  isClearable = false,
  selectedValue,
  disable = false,
  sortType = "alphabetical", // Days Orderwise display issue solved based on this flag default display alphabetical otherwise given order display
  "aria-label": ariaLabelProp,
  "aria-required": ariaRequiredProp,
}) => {
  const containerRef = useRef(null);
  const isRequired = ariaRequiredProp !== undefined ? ariaRequiredProp : required;
  const resolvedAriaLabel = ariaLabelProp || ariaLabel;
  const selectAllAriaLabel =
    resolvedAriaLabel
      ? `Select all ${resolvedAriaLabel}`
      : typeof label === "string" && label.trim()
        ? `Select all ${label.trim()}`
        : "Select all";

  const dropdownAriaLabel =
    resolvedAriaLabel ||
    (typeof label === "string" && label.trim() ? label.trim() : "") ||
    (typeof placeholder === "string" && placeholder.trim() ? placeholder.trim() : "") ||
    (typeof name === "string" && name.trim() ? name.trim() : "") ||
    "Select options";

  const inputId = (id || `${name}-multiselect`) + "_input";

  const [selectedValues, setSelectedValues] = useState(selectedValue || []);

  useEffect(() => {
    setSelectedValues(selectedValue || []);
  }, [selectedValue]);

  useEffect(() => {
    handleChange([]);
  }, [isClearable]);

  useEffect(() => {
    const inputElement =
      containerRef.current?.querySelector("input.searchBox") ||
      document.getElementById(inputId);

    if (inputElement) {
      inputElement.setAttribute("aria-label", dropdownAriaLabel);
      if (isRequired) {
        inputElement.setAttribute("aria-required", "true");
      } else {
        inputElement.removeAttribute("aria-required");
      }
    }
  }, [dropdownAriaLabel, isRequired, inputId, selectedValues]);

  const handleSelectAllToggle = () => {
    if (selectedValues.length === options.length) {
      handleChange([]);
    } else {
      setSelectedValues(options);
      handleChange(options);
    }
  };

  const handleSelect = (selectedList) => {
    setSelectedValues(selectedList);
    handleChange(selectedList);
  };

  const handleRemove = (selectedList) => {
    setSelectedValues(selectedList);
    handleChange(selectedList);
  };

  // ✅ Error detection for this specific field name
  const hasError = Array.isArray(errorMessage)
    ? errorMessage.some(
      (err) => err?.path?.includes(name) || err?.context?.key === name,
    )
    : Boolean(errorMessage?.[name]);

  const fieldErrorMessage = Array.isArray(errorMessage)
    ? errorMessage
      .filter(
        (err) => err?.path?.includes(name) || err?.context?.key === name,
      )
      .map((err) => err?.message)
      .filter(Boolean)
      .join(" ")
    : errorMessage?.[name] || "";

  const handleKeyDown = () => {
    setTimeout(() => {
      const activeOption = document.querySelector(".highlightOption");

      if (activeOption) {
        activeOption.scrollIntoView({
          block: "nearest",
        });
      }
    }, 0);
  };

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        if (onBlur) {
          setTimeout(() => {
            onBlur(selectedValues);
          }, 0);
        }
      }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex gap-5 align-items-center white-space-nowrap">
          {label && (
            <label
              htmlFor={inputId}
              className="form-label font-size-14 form-label-custom-visible m-0"
            >
              {label} {isRequired && <span className="text-danger">*</span>}
            </label>
          )}
        </div>
        <div className="d-flex gap-5 align-items-center white-space-nowrap">
          <label
            htmlFor={`${name}-selectAllCheckbox`}
            className="text-black d-flex align-items-center cursor-pointer"
          >
            <input
              type="checkbox"
              id={`${name}-selectAllCheckbox`}
              checked={
                selectedValues.length === options.length && options.length > 0
              }
              onChange={handleSelectAllToggle}
              className="me-1"
              aria-label={selectAllAriaLabel}
            />
            Select All
          </label>
        </div>
      </div>

      <Multiselect
        id={id || `${name}-multiselect`}
        aria-label={dropdownAriaLabel}
        // options={[...options].sort((a, b) =>
        //   a[displayValue].localeCompare(b[displayValue])
        // )}
        options={
          sortType === "alphabetical"
            ? [...options].sort((a, b) =>
              a[displayValue].localeCompare(b[displayValue]),
            )
            : [...options] // keep given order
        }
        selectedValues={selectedValues}
        onSelect={handleSelect}
        onRemove={handleRemove}
        // uniqueKey="label"
        displayValue={displayValue}
        placeholder={selectedValues?.length > 0 ? "" : placeholder}
        showCheckbox={showCheckbox}
        showArrow={true}
        customArrow={
          <span>
            <img
              className="icon_cancel icon_down_dir"
              src="data:image/svg+xml,%3Csvg%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%20width%3D%2232%22%20%20%20%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%20%20%20%20%3Cg%20id%3D%22background%22%3E%20%20%20%20%20%20%20%20%3Crect%20fill%3D%22none%22%20height%3D%2232%22%20width%3D%2232%22%2F%3E%20%20%20%20%3C%2Fg%3E%20%20%20%20%3Cg%20id%3D%22arrow_x5F_down%22%3E%20%20%20%20%20%20%20%20%3Cpolygon%20points%3D%222.002%2C10%2016.001%2C24%2030.002%2C10%20%20%22%2F%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E"
              role="presentation"
              alt=""
              aria-hidden="true"
            />
          </span>
        }

        className={`bg-white ${hasError ? "multiSelectContainerError" : ""}`}
        closeOnSelect={false}
        disable={disable}
      />

      {hasError && (
        <div className="text-danger fw-700 fs-6">{fieldErrorMessage}</div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
